import {
  Box,
  Checkbox,
  FormControl,
  Icon,
  Radio,
  Spinner,
  Text,
  TextFieldSize,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import { parse } from 'csv-parse/browser/esm'; // Import the browser version for client-side usage
import { map } from 'lodash';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileUploadInput } from '~/components';
import { PrimaryButtonStyled, SecondaryButtonStyled } from '~/styles';
import { mediaServiceBasePaths } from '~/types/common';
import axiosInstance from '~/utilities/axiosInstance';
import { FooterStyled, ToolCatalogsBoxStyled, ToolCatalogsContainer } from './styled';
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export const ToolCatalogs: FC = () => {
  const { t } = useTranslation();
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([]);
  const [duplicateDataOption, setDuplicateDataOption] = useState<string>('');
  const [appendRecords, setAppendRecords] = useState<boolean>(false);
  const dispatchToast = useToastsDispatch();
  const [download, setDownload] = useState<boolean>(false);

  const duplicateRecordsOptions = [
    { id: 'SKIP', option: 'SKIP_DUPLICATE_RECORDS' },
    { id: 'UPDATE', option: 'UPDATE_WHERE_DATA_IS_DIFFERENT' },
    { id: 'OVERWRITE', option: 'OVERWRITE_EXISTING_RECORDS' },
  ];
  const acceptedFileTypes = ['text/csv'];
  const expectedColumns = ['BSCN_PART_NUMBER', 'IS_LOCKED', 'LIMIT_COUNT', 'IS_ACTIVE', 'DESCRIPTION'];
  const [fileName, setFileName] = useState('');
  const handleRadioBtnChange = (selectedValue: string) => {
    setDuplicateDataOption(selectedValue);
  };
  const validateColumns = (columns: string[]): boolean => {
    // Check if the columns match the expected order and names
    if (columns.length !== expectedColumns.length) return false;
    for (let i = 0; i < expectedColumns.length; i++) {
      if (columns[i] !== expectedColumns[i]) return false;
    }
    return true;
  };
  const submitFile = async () => {
    setLoading(true);
    if (loading) return;
    const reader = new FileReader();
    reader.readAsText(files[0]);
    reader.onload = async (e) => {
      const csvData = e.target?.result;
      if (typeof csvData === 'string') {
        // Parse the CSV string
        parse(
          csvData,
          {
            delimiter: ',',
            columns: true,
            skip_empty_lines: true,
          },
          async (err, records) => {
            try {
              if (err) {
                setError(t('CSV_FILE_ERROR'));
                setLoading(false);
                return;
              }

              if (records.length <= 0) {
                setError(t('EMPTY_CSV'));
                setLoading(false);
                return;
              }
              // Extract the header (first row) to validate
              const header = Object.keys(records[0]); // Get columns from the first record
              if (!validateColumns(header)) {
                setError(t('CSV_INVALID_FORMAT'));
                setLoading(false);
                return;
              }
              const formData = new FormData();
              formData.append('file', files[0]);
              formData.append('actionPerformed', duplicateDataOption);
              formData.append('appendRecord', appendRecords.toString());

              axiosInstance
                .post(mediaServiceBasePaths.projectCatalogUpload, formData)
                .then((response) => {
                  setLoading(false);
                  if (response.data.error) {
                    const uploadErrorToast: ToastAction = {
                      type: 'ADD_TOAST',
                      payload: {
                        toastType: ToastType.DANGER,
                        content: t('CSV_FILE_ERROR'),
                        autoDismissTimeout: 3000,
                      },
                    };
                    dispatchToast(uploadErrorToast);
                  }

                  const uploadSuccessToast: ToastAction = {
                    type: 'ADD_TOAST',
                    payload: {
                      toastType: ToastType.SUCCESS,
                      content: t('CSV_FILE_UPLOAD_SUCCESS'),
                      autoDismissTimeout: 3000,
                    },
                  };
                  reset();
                  dispatchToast(uploadSuccessToast);
                })
                .catch((error) => {
                  setLoading(false);
                  const uploadErrorToast: ToastAction = {
                    type: 'ADD_TOAST',
                    payload: {
                      toastType: ToastType.DANGER,
                      content: t('CSV_FILE_ERROR'),
                      autoDismissTimeout: 3000,
                    },
                  };
                  dispatchToast(uploadErrorToast);
                });
            } catch (error) {
              setLoading(false);
              const uploadErrorToast: ToastAction = {
                type: 'ADD_TOAST',
                payload: {
                  toastType: ToastType.DANGER,
                  content: t('CSV_FILE_ERROR'),
                  autoDismissTimeout: 3000,
                },
              };
              dispatchToast(uploadErrorToast);
            }
          }
        );
      }
    };
  };

  const downloadFile = async () => {
    setDownload(true);
    const uploadErrorToast: ToastAction = {
      type: 'ADD_TOAST',
      payload: {
        toastType: ToastType.DANGER,
        content: t('CSV_DOWNLOAD_ERROR'),
        autoDismissTimeout: 3000,
      },
    };
    try {
      axiosInstance
        .get(`${mediaServiceBasePaths.projectToolCatalogExport}`, {
          responseType: 'blob',
        })
        .then((response) => {
          setDownload(false);
          if (!response) {
            dispatchToast(uploadErrorToast);
            return;
          }

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.download = 'ProjectToolCatalogs.csv';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          setDownload(false);
          dispatchToast(uploadErrorToast);
        });
    } catch (error) {
      setDownload(false);
      dispatchToast(uploadErrorToast);
    }
  };
  const handleFileUpload = (acceptedFiles: File[]) => {
    setFiles(acceptedFiles); // Update state with accepted files
    setFileName(acceptedFiles[0]?.path || '');
  };
  const reset = () => {
    setError('');
    setFileName('');
    setFiles([]);
    setDuplicateDataOption('');
    setAppendRecords(false);
  };
  return (
    <ToolCatalogsBoxStyled>
      <ToolCatalogsContainer>
        <Box>
          <Text size="500" weight="600">
            {t('PROJECT_TOOL_CATALOG')}
          </Text>
        </Box>
        <Box
          flexDirection={{ sm: 'column' }}
          mr={{ sm: '500' }}
          p={{ sm: '500' }}
          justifyContent={{ sm: 'space-between' }}
          gap={{ sm: '500' }}
        >
          <Box mt={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
            <FormControl label={t('PROJECT_TOOL_CATALOG')}>
              <FileUploadInput
                onDrop={handleFileUpload}
                acceptFileTypes={acceptedFileTypes}
                fileName={fileName}
                key="ToolCatalogFileUpload"
              />
            </FormControl>

            <Box justifyContent={{ sm: 'center' }}>
              <Text color="red.500"> {error}</Text>
            </Box>
          </Box>

          <Box gap={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
            <Text size="400" color="grey.800" weight="600">
              {t('Duplicate_Records')} :
            </Text>
            {map(duplicateRecordsOptions, (item) => {
              return (
                <Box gap={{ sm: '400' }}>
                  <Radio
                    style={{ cursor: 'pointer' }}
                    onChange={() => handleRadioBtnChange(item.id)}
                    name="uploadOptions"
                    size={TextFieldSize.SMALL}
                    id={item.id}
                    value={item.id}
                    checked={item.id == duplicateDataOption}
                  />
                  <Text size="400" color="grey.800">
                    {t(item.option)}
                  </Text>
                </Box>
              );
            })}
          </Box>
          <Box>
            <Checkbox
              checked={appendRecords}
              readOnly
              onChange={() => {
                setAppendRecords(!appendRecords);
              }}
            />
            <Box ml={{ sm: '400' }}>
              <Text color="grey.800" size="400">
                {t('APPEND_NEW_RECORDS')}
              </Text>
            </Box>
          </Box>
        </Box>
      </ToolCatalogsContainer>
      <FooterStyled alignItems={{ sm: 'center' }}>
        <PrimaryButtonStyled type="button" onClick={downloadFile} disabled={download}>
          <Box gap={{ sm: '400' }}>
            <Text color="white" size="300">
              {t('Download')}
            </Text>

            {download ? <Spinner /> : <Icon icon="download"></Icon>}
          </Box>
        </PrimaryButtonStyled>

        <Box gap={{ sm: '200' }}>
          <PrimaryButtonStyled
            type="button"
            onClick={() => {
              submitFile();
            }}
            disabled={files.length <= 0 || duplicateDataOption.length <= 0 || loading}
          >
            {t('SUBMIT')}

            {loading && <Spinner />}
          </PrimaryButtonStyled>
          <SecondaryButtonStyled
            type="button"
            onClick={() => {
              reset();
            }}
          >
            <Text size="300">{t('CLEAR')}</Text>
          </SecondaryButtonStyled>
        </Box>
      </FooterStyled>
    </ToolCatalogsBoxStyled>
  );
};
